import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CurrencyPipe } from '@angular/common';

import { nonNegativeCents } from '@app/sales/reglement/reglement.models';
import { CashCountGridComponent, CashCountResult } from '@app/sales/reglement/cash-count-grid/cash-count-grid.component';
import { CentsInputDirective } from '@app/sales/reglement/cents-input.directive';

export interface OpenFloatData {
	// « Fond laissé à la clôture du … » ; absent si la dernière clôture est illisible.
	label: string | null;
	// Montant proposé (attendu du serveur) ; null : champ vide.
	proposed: number | null;
}

@Component({
	selector: 'app-open-float-dialog',
	standalone: true,
	templateUrl: './open-float-dialog.component.html',
	styleUrls: ['./open-float-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, CurrencyPipe, CashCountGridComponent, CentsInputDirective],
})
export class OpenFloatDialogComponent implements AfterViewInit {

	public dialogRef = inject(MatDialogRef<OpenFloatDialogComponent, number>);
	public data: OpenFloatData = inject(MAT_DIALOG_DATA);
	@ViewChild(CashCountGridComponent, { static: true }) grid!: CashCountGridComponent;

	// Même règle que le serveur (refus 400) et que le fond compté au Z : ≥ 0, deux décimales au plus, sous le plafond.
	public amount = new FormControl<number | null>(this.data.proposed, [Validators.required, nonNegativeCents]);
	public form = new FormGroup({ amount: this.amount });
	// Montant avant la première case remplie (proposé ou tapé) : une grille revidée le rend au lieu d'un 0. undefined : aucun comptage en cours.
	private beforeCount: number | null | undefined;

	ngAfterViewInit(): void {
		this.grid.focusFirst();
	}

	onCount(r: CashCountResult | null): void {
		if (!r) return;
		if (!this.grid.touched) {
			if (this.beforeCount !== undefined) { this.amount.setValue(this.beforeCount); this.beforeCount = undefined; }
			return;
		}
		if (this.beforeCount === undefined) this.beforeCount = this.amount.value;
		this.amount.setValue(r.total);
		this.amount.markAsTouched();
	}

	get differs(): boolean {
		return this.grid.touched && !this.grid.invalid && this.amount.valid && Number(this.amount.value) !== this.grid.total;
	}

	// Une ligne de grille refusée bloque aussi : le champ porterait le total d'avant, sans elle.
	get canConfirm(): boolean {
		return this.amount.valid && !this.grid.invalid;
	}

	confirm(): void {
		if (!this.canConfirm) { this.amount.markAsTouched(); return; }
		this.dialogRef.close(Number(this.amount.value));
	}

	// Annuler, croix, Échap : aucun montant rendu, donc aucune ouverture envoyée (un mat-dialog-close nu rendrait '').
	cancel(): void {
		this.dialogRef.close();
	}

	// Entrée en fin de grille : vers le montant s'il manque encore, sinon vers le bouton.
	focusSubmit(): void {
		if (this.amount.invalid) this.amount.markAsTouched();
		document.getElementById(this.amount.invalid ? 'open-float' : 'open-submit')?.focus();
	}
}
